import debug from '@techstyle/debug';

import { useCioBrowseContext } from '../context/CIOBrowseProvider';
import { useCioSearchContext } from '../context/CIOSearchProvider';
import { useProductsV2Context } from '../context/ProductsV2Provider';
import { UnifiedProductGridResponse } from '../types/gridResponses';

/**
 * Hook to unify product grid data from multiple API sources.
 * UnifiedProductGridResponse ensures that the returned data conforms to a consistent structure
 * regardless of the source context. This allows UI components to consume product grid data
 * without needing to know the underlying API source.
 *
 * Transformations and data merging should be handled in their respective context providers,
 * specifically in the `select` option of react-query's useQuery/useInfiniteQuery,
 * to optimize performance and minimize unnecessary re-renders, leveraging react-query's caching and memoization.
 *
 * We expect only one of the contexts to be active at a time, so the order of checks is not significant.
 *
 * @returns {UnifiedProductGridResponse} The unified product grid data from the active context.
 * @throws {Error} If no product grid context is found.
 */
const useUnifiedProductGridData = (): UnifiedProductGridResponse => {
  const cioBrowseData = useCioBrowseContext();
  const cioSearchData = useCioSearchContext();
  const productsV2Data = useProductsV2Context();

  if (cioBrowseData) {
    return cioBrowseData;
  }
  debug('CIO Browse data is not available in context');

  if (cioSearchData) {
    return cioSearchData;
  }
  debug('CIO Search data is not available in context');

  if (productsV2Data) {
    return productsV2Data;
  }
  debug('Products V2 data is not available in context');

  throw new Error('No Product Grid context found');
};

export default useUnifiedProductGridData;
